import { WithDisposable } from '@blocksuite/global/lit';
import { CheckBoxCheckSolidIcon, CheckBoxUnIcon } from '@blocksuite/icons/lit';
import { ShadowlessElement } from '@blocksuite/std';
import { css, html } from 'lit';
import { property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';

import type { SelectionModifiers } from './types.js';

/**
 * View-agnostic row multi-select checkbox. Hosts pass a plain
 * `selected` boolean (so the checkbox stays a dumb, reactive leaf)
 * plus an `onToggle` callback — any view that speaks
 * `RowSelectionController` can render the same pill without
 * re-implementing the visuals or the click rules.
 *
 * Click behaviour:
 *   plain  → ctrl-toggle the row (add if missing, drop if selected)
 *   shift  → range-select from the controller's anchor to this row
 *
 * The host is responsible for anything beyond membership — including
 * area-selection sync, Kanban group scoping, or tagging a "selection
 * source" for downstream shortcut routing.
 */
export class DvRowSelectCheckbox extends WithDisposable(ShadowlessElement) {
  /*
   * Visibility is host-driven: the checkbox is opacity 0 by default
   * and hosts opt it in via a row-level `:hover` rule (plus the
   * `.selected` class toggling it on permanently). An earlier
   * `.row-select-checkbox:hover` rule tried to gate on the element
   * itself — but hovering an opacity:0 element is effectively
   * invisible to the user, so row-level hover is the only reliable
   * affordance.
   */
  static override styles = css`
    dv-row-select-checkbox {
      display: contents;
    }
    .row-select-checkbox {
      display: flex;
      align-items: center;
      background: transparent;
      opacity: 0;
      cursor: pointer;
      font-size: 20px;
      color: var(--streamlain-icon-color);
    }
    .row-select-checkbox.selected {
      opacity: 1;
    }
  `;

  @property({ attribute: false })
  accessor selected = false;

  @property({ attribute: false })
  accessor onToggle!: (mods: SelectionModifiers) => void;

  override connectedCallback() {
    super.connectedCallback();
    this.disposables.addFromEvent(this, 'click', (e: MouseEvent) => {
      e.stopPropagation();
      if (e.shiftKey) {
        this.onToggle({ shift: true });
      } else {
        this.onToggle({ ctrl: true });
      }
    });
  }

  override render() {
    const classString = classMap({
      'row-selected-bg': true,
      'row-select-checkbox': true,
      selected: this.selected,
    });
    return html`
      <div class=${classString}>
        ${this.selected
          ? CheckBoxCheckSolidIcon({ style: `color:#1E96EB` })
          : CheckBoxUnIcon()}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-row-select-checkbox': DvRowSelectCheckbox;
  }
}
